// WebAudioEngine.js - Native Web Audio API implementation for DAW
// Replaces Tone.js with direct Web Audio API for better control
import { drumSampleFiles } from '../constants/drumSamples';
import { instrumentConfigs } from '../constants/instrumentConfigs';
import { calculateTimelineLength } from '../utils/audioUtils';
import Soundfont from 'soundfont-player';

export class WebAudioEngine {
    constructor() {
        this.context = null;
        this.masterGain = null;
        this.tracks = new Map(); // Track ID -> Track audio graph
        this.players = new Map(); // Clip ID -> Audio player data
        this.synths = new Map(); // Track ID -> Synth data
        this.activeVoices = new Set(); // Scheduled/playing one-shots and notes
        this.isPlaying = false;
        this.startTime = 0;
        this.playbackTime = 0;
        this.activePlayerCount = 0;
        this.onPlaybackEnd = null;
        this.playbackSessionId = 0;
        this.keepAlive = false;

        // Drum sample buffers (filled after initialize)
        this.drumBuffers = {}; // name -> AudioBuffer
        this.drumSamplesLoaded = false;

        // Soundfont instruments - support multiple instruments
        this.soundfontInstruments = {}; // instrumentName -> Soundfont instance
        this.soundfontLoading = {}; // instrumentName -> Promise
    }

    registerVoice(voice) {
        if (!voice || typeof voice.stop !== 'function') return;
        this.activeVoices.add(voice);
    }

    unregisterVoice(voice) {
        this.activeVoices.delete(voice);
    }

    stopAllVoices() {
        if (!this.context) return;
        const now = this.context.currentTime;

        this.activeVoices.forEach(voice => {
            try {
                voice.stop(now);
            } catch (e) {
                // Ignore stop errors (e.g., stopping before start)
            }
        });
        this.activeVoices.clear();
    }

    // Initialize audio context (call after user interaction)
    async initialize() {
        if (this.context) {
            await this.context.resume();
            return this.context;
        }

        // Create audio context
        this.context = new (window.AudioContext || window.webkitAudioContext)();

        // Create master bus (input gain), a master limiter, and a post-boost stage
        // Many users reported overall levels being too quiet even with track faders up.
        // We keep the existing master gain as the bus input (so existing code keeps working),
        // then add a transparent limiter and a fixed boost to raise overall loudness safely.
        this.masterGain = this.context.createGain();
        this.masterGain.gain.value = 0.8; // Bus level controlled by UI via setMasterVolume

        // Master limiter (acts as safety/soft limiter)
        this.masterLimiter = this.context.createDynamicsCompressor();
        // Limiter-like settings
        this.masterLimiter.threshold.value = -3;   // start compressing close to 0 dBFS
        this.masterLimiter.knee.value = 0;         // hard knee
        this.masterLimiter.ratio.value = 20;       // high ratio for limiting
        this.masterLimiter.attack.value = 0.003;   // fast attack
        this.masterLimiter.release.value = 0.25;   // moderate release

        // Fixed post-boost to increase perceived loudness without changing UI faders
        // ~+4 dB (1.6x). The limiter above will catch peaks to avoid clipping.
        this.masterBoost = this.context.createGain();
        this.masterBoost.gain.value = 1.6;

        // Connect master chain: tracks → masterGain → limiter → boost → destination
        this.masterGain.connect(this.masterLimiter);
        this.masterLimiter.connect(this.masterBoost);
        this.masterBoost.connect(this.context.destination);

        console.log('🎛️ Web Audio Engine initialized');

        // Preload drum samples
        try {
            await this.loadDrumSamples();
        } catch (e) {
            console.warn('Failed loading drum samples:', e);
        }

        // Preload default soundfont instrument (piano)
        try {
            await this.loadSoundfontInstrument('acoustic_grand_piano');
        } catch (e) {
            console.warn('Failed loading soundfont piano:', e);
        }

        return this.context;
    }

    // Load soundfont instrument
    async loadSoundfontInstrument(instrumentName) {
        if (this.soundfontInstruments[instrumentName]) {
            return this.soundfontInstruments[instrumentName];
        }

        if (this.soundfontLoading[instrumentName]) {
            return this.soundfontLoading[instrumentName];
        }

        console.log(`🎵 Loading Soundfont ${instrumentName}...`);

        // Use Soundfont.instrument() as per documentation
        this.soundfontLoading[instrumentName] = Soundfont.instrument(this.context, instrumentName, {
            soundfont: 'MusyngKite',
            format: 'mp3'
        }).then(instrument => {
            this.soundfontInstruments[instrumentName] = instrument;
            this.soundfontLoading[instrumentName] = null;
            console.log(`✅ Soundfont ${instrumentName} loaded successfully`);
            return instrument;
        }).catch(err => {
            console.error(`❌ Failed to load soundfont ${instrumentName}:`, err);
            this.soundfontLoading[instrumentName] = null;
            throw err;
        });

        return this.soundfontLoading[instrumentName];
    }

    // Load drum samples from imported file URLs and decode into AudioBuffers
    async loadDrumSamples() {
        if (!this.context) return;
        const entries = Object.entries(drumSampleFiles || {});
        if (entries.length === 0) {
            console.warn('No drum sample files found to load');
            return;
        }

        const decodePromises = entries.map(async ([name, url]) => {
            try {
                const resp = await fetch(url);
                const arrayBuf = await resp.arrayBuffer();
                const audioBuf = await this.context.decodeAudioData(arrayBuf);
                this.drumBuffers[name] = audioBuf;
                return true;
            } catch (err) {
                console.warn(`Failed to load sample "${name}" from ${url}:`, err);
                return false;
            }
        });

        await Promise.all(decodePromises);
        this.drumSamplesLoaded = true;
        console.log('🥁 Drum samples loaded:', Object.keys(this.drumBuffers));
    }

    // Set master volume
    setMasterVolume(volume) {
        if (!this.masterGain) return;
        this.masterGain.gain.linearRampToValueAtTime(
            volume,
            this.context.currentTime + 0.1
        );
    }

    // Create or get track audio graph
    getTrackGraph(trackId) {
        if (this.tracks.has(trackId)) {
            return this.tracks.get(trackId);
        }

        // Create new track audio graph
        const inputBus = this.context.createGain();
        const eq = this.createEQ();
        const compressor = this.createCompressor();
        const distortion = this.createDistortion();
        if (!distortion) {
            console.error('TRACK GRAPH: Failed to create distortion, aborting track graph creation');
            return null;
        }
        const chorus = this.createChorus();
        const delay = this.createDelay();
        const reverb = this.createReverb();

        // Create stereo panner with explicit options - IMPORTANT: this is the critical node for panning
        const pannerOptions = { pan: 0 };
        console.log("Creating new StereoPannerNode with options:", pannerOptions);
        const stereoPanner = new StereoPannerNode(this.context, pannerOptions);

        const volume = this.context.createGain();

        // Set default values
        volume.gain.value = 0.8;

        // Connect nodes in the audio processing chain
        console.log(`TRACK GRAPH: Creating audio chain for track ${trackId}`);
        inputBus.connect(eq.input);
        eq.output.connect(compressor.input);
        compressor.output.connect(distortion.input);
        distortion.output.connect(chorus.input);
        chorus.output.connect(delay.input);
        delay.output.connect(reverb.input);
        reverb.output.connect(stereoPanner);
        stereoPanner.connect(volume);
        volume.connect(this.masterGain);

        console.log(`TRACK GRAPH: Audio chain connected - inputBus → EQ → Compressor → Distortion → Chorus → Delay → Reverb → Panner → Volume → Master`);

        const graph = {
            inputBus,
            eq,
            compressor,
            distortion,
            chorus,
            delay,
            reverb,
            stereoPanner,
            volume
        };

        this.tracks.set(trackId, graph);
        return graph;
    }

    // Create a 3-band EQ
    createEQ() {
        const input = this.context.createGain();
        const output = this.context.createGain();

        const lowShelf = this.context.createBiquadFilter();
        lowShelf.type = 'lowshelf';
        lowShelf.frequency.value = 320;
        lowShelf.gain.value = 0;

        const mid = this.context.createBiquadFilter();
        mid.type = 'peaking';
        mid.frequency.value = 1000;
        mid.Q.value = 0.5;
        mid.gain.value = 0;

        const highShelf = this.context.createBiquadFilter();
        highShelf.type = 'highshelf';
        highShelf.frequency.value = 3200;
        highShelf.gain.value = 0;

        // Connect EQ chain
        input
            .connect(lowShelf)
            .connect(mid)
            .connect(highShelf)
            .connect(output);

        return {
            input,
            output,
            low: lowShelf.gain,
            mid: mid.gain,
            high: highShelf.gain
        };
    }

    // Create compressor effect with makeup gain and output gain
    createCompressor() {
        const input = this.context.createGain();
        const output = this.context.createGain();

        // Create the dynamics compressor
        const dynamicsCompressor = this.context.createDynamicsCompressor();

        // Create makeup gain (post-compression gain to compensate for volume reduction)
        const makeupGain = this.context.createGain();

        // Create output gain (final output level control)
        const outputGain = this.context.createGain();

        // Set default compressor values
        dynamicsCompressor.threshold.value = -24;    // -24 dB threshold
        dynamicsCompressor.ratio.value = 4;          // 4:1 ratio
        dynamicsCompressor.attack.value = 0.003;     // 3ms attack
        dynamicsCompressor.release.value = 0.25;     // 250ms release
        dynamicsCompressor.knee.value = 30;          // 30 dB knee (smooth compression)

        // Set default gain values
        makeupGain.gain.value = 1;                   // 0 dB makeup gain
        outputGain.gain.value = 1;                   // 0 dB output gain

        // Connect the compressor chain
        input.connect(dynamicsCompressor);
        dynamicsCompressor.connect(makeupGain);
        makeupGain.connect(outputGain);
        outputGain.connect(output);

        console.log(`COMPRESSOR: Created compressor with threshold=${dynamicsCompressor.threshold.value}dB, ratio=${dynamicsCompressor.ratio.value}:1`);

        return {
            input,
            output,
            dynamicsCompressor,
            makeupGain,
            outputGain,
            setThreshold(value) {
                dynamicsCompressor.threshold.linearRampToValueAtTime(value, this.context?.currentTime + 0.05 || 0);
                console.log(`COMPRESSOR: Setting threshold to ${value}dB`);
            },
            setRatio(value) {
                dynamicsCompressor.ratio.value = value;
                console.log(`COMPRESSOR: Setting ratio to ${value}:1`);
            },
            setAttack(value) {
                dynamicsCompressor.attack.value = value;
                console.log(`COMPRESSOR: Setting attack to ${value * 1000}ms`);
            },
            setRelease(value) {
                dynamicsCompressor.release.value = value;
                console.log(`COMPRESSOR: Setting release to ${value * 1000}ms`);
            },
            setKnee(value) {
                dynamicsCompressor.knee.value = value;
                console.log(`COMPRESSOR: Setting knee to ${value}dB`);
            },
            setMakeupGain(value) {
                // Convert from dB to linear gain
                const linearGain = Math.pow(10, value / 20);
                makeupGain.gain.linearRampToValueAtTime(linearGain, this.context?.currentTime + 0.05 || 0);
                console.log(`COMPRESSOR: Setting makeup gain to ${value}dB (${linearGain.toFixed(3)} linear)`);
            },
            setOutputGain(value) {
                // Convert from dB to linear gain
                const linearGain = Math.pow(10, value / 20);
                outputGain.gain.linearRampToValueAtTime(linearGain, this.context?.currentTime + 0.05 || 0);
                console.log(`COMPRESSOR: Setting output gain to ${value}dB (${linearGain.toFixed(3)} linear)`);
            }
        };
    }

    // Create distortion effect with waveshaping
    createDistortion() {
        if (!this.context) {
            console.error('DISTORTION: AudioContext is not available');
            return null;
        }

        const input = this.context.createGain();
        const output = this.context.createGain();

        // Pre-gain stage (input gain)
        const preGain = this.context.createGain();

        // Waveshaper for distortion
        const waveshaper = this.context.createWaveShaper();

        // Post-gain stage (drive control)
        const driveGain = this.context.createGain();

        // Tone control (high-frequency filter)
        const toneFilter = this.context.createBiquadFilter();
        toneFilter.type = 'lowpass';
        toneFilter.frequency.value = 5000; // Default tone
        toneFilter.Q.value = 1;

        // Output level control
        const outputLevel = this.context.createGain();

        // Set default values
        preGain.gain.value = 1;        // 0dB pre-gain
        driveGain.gain.value = 1;      // 0dB drive
        // Previously this was 0.7, which quietly attenuated every track even in 'clean' mode.
        // Use unity gain here so tracks aren't quieter unless distortion is actively applied.
        outputLevel.gain.value = 1;    // Unity output level in clean mode

        // Create distortion curve
        this.updateDistortionCurve(waveshaper, 'clean');

        // Connect the distortion chain
        input.connect(preGain);
        preGain.connect(waveshaper);
        waveshaper.connect(driveGain);
        driveGain.connect(toneFilter);
        toneFilter.connect(outputLevel);
        outputLevel.connect(output);

        console.log(`DISTORTION: Created distortion with preGain=0dB, drive=0dB, tone=5000Hz`);

        // Store reference to the audio context for the distortion methods
        const audioContext = this.context;

        return {
            input,
            output,
            preGain,
            waveshaper,
            driveGain,
            toneFilter,
            outputLevel,
            currentMode: 'clean',
            setPreGain(value) {
                try {
                    // Convert from dB to linear gain
                    const linearGain = Math.pow(10, value / 20);
                    const currentTime = audioContext?.currentTime || 0;
                    preGain.gain.linearRampToValueAtTime(linearGain, currentTime + 0.05);
                    console.log(`DISTORTION: Setting pre-gain to ${value}dB (${linearGain.toFixed(3)} linear)`);
                } catch (error) {
                    console.error('DISTORTION: Error setting pre-gain:', error);
                }
            },
            setDrive(value) {
                try {
                    // Convert from dB to linear gain
                    const linearGain = Math.pow(10, value / 20);
                    const currentTime = audioContext?.currentTime || 0;
                    driveGain.gain.linearRampToValueAtTime(linearGain, currentTime + 0.05);
                    console.log(`DISTORTION: Setting drive to ${value}dB (${linearGain.toFixed(3)} linear)`);
                } catch (error) {
                    console.error('DISTORTION: Error setting drive:', error);
                }
            },
            setTone(value) {
                try {
                    // Tone control: 200Hz to 8000Hz
                    const frequency = Math.max(200, Math.min(8000, value));
                    const currentTime = audioContext?.currentTime || 0;
                    toneFilter.frequency.linearRampToValueAtTime(frequency, currentTime + 0.05);
                    console.log(`DISTORTION: Setting tone filter to ${frequency}Hz`);
                } catch (error) {
                    console.error('DISTORTION: Error setting tone:', error);
                }
            },
            setMode(mode) {
                try {
                    this.currentMode = mode;
                    // Use the WebAudioEngine instance to call the method
                    const engine = window.webAudioEngine || this;
                    if (engine && typeof engine.updateDistortionCurve === 'function') {
                        engine.updateDistortionCurve(waveshaper, mode);
                        console.log(`DISTORTION: Setting mode to ${mode}`);
                    } else {
                        console.error('DISTORTION: Could not find updateDistortionCurve method');
                    }
                } catch (error) {
                    console.error('DISTORTION: Error setting mode:', error);
                }
            }
        };
    }

    // Helper method to create different distortion curves
    updateDistortionCurve(waveshaper, mode) {
        const samples = 44100;
        const curve = new Float32Array(samples);
        const deg = Math.PI / 180;

        for (let i = 0; i < samples; i++) {
            const x = (i * 2) / samples - 1;

            switch (mode) {
                case 'clean':
                    // Very light saturation
                    curve[i] = x * 0.9;
                    break;

                case 'overdrive':
                    // Smooth overdrive using tanh
                    curve[i] = Math.tanh(x * 3) * 0.7;
                    break;

                case 'distortion':
                    // Hard clipping distortion
                    curve[i] = Math.max(-0.8, Math.min(0.8, x * 5));
                    break;

                case 'nasty':
                    // Asymmetric harsh distortion
                    if (x < 0) {
                        curve[i] = Math.max(-0.9, x * 4);
                    } else {
                        curve[i] = Math.min(0.7, Math.pow(x, 0.7) * 2);
                    }
                    break;

                default:
                    curve[i] = x;
            }
        }

        waveshaper.curve = curve;
        waveshaper.oversample = '4x'; // High quality oversampling
        console.log(`DISTORTION: Updated curve for ${mode} mode`);
    }

    // Create chorus effect
    createChorus() {
        const input = this.context.createGain();
        const output = this.context.createGain();
        const wet = this.context.createGain();
        const dry = this.context.createGain();

        // Set initial mix
        wet.gain.value = 0;
        dry.gain.value = 1;

        // Create delay nodes for chorus
        const delayNode1 = this.context.createDelay();
        const delayNode2 = this.context.createDelay();
        delayNode1.delayTime.value = 0.03;
        delayNode2.delayTime.value = 0.04;

        // Create LFOs for chorus
        const lfo1 = this.context.createOscillator();
        const lfo2 = this.context.createOscillator();
        const lfoGain1 = this.context.createGain();
        const lfoGain2 = this.context.createGain();

        lfo1.type = 'sine';
        lfo2.type = 'sine';
        lfo1.frequency.value = 0.1;
        lfo2.frequency.value = 0.15;
        lfoGain1.gain.value = 0.002;
        lfoGain2.gain.value = 0.003;

        // Connect LFOs to delay times
        lfo1.connect(lfoGain1).connect(delayNode1.delayTime);
        lfo2.connect(lfoGain2).connect(delayNode2.delayTime);

        // Start oscillators
        lfo1.start();
        lfo2.start();

        // Connect input to both wet and dry paths
        input.connect(dry);
        input.connect(delayNode1);
        input.connect(delayNode2);

        // Connect delay nodes to wet output
        delayNode1.connect(wet);
        delayNode2.connect(wet);

        // Mix wet and dry to output
        dry.connect(output);
        wet.connect(output);

        return {
            input,
            output,
            wet: wet.gain,
            lfo1,
            lfo2,
            lfoGain1,
            lfoGain2,
            setWet(value) {
                wet.gain.linearRampToValueAtTime(value, this.context?.currentTime + 0.1 || 0);
                dry.gain.linearRampToValueAtTime(1 - value, this.context?.currentTime + 0.1 || 0);
            },
            setRate(value) {
                // Set both LFOs to slightly different rates for a richer chorus effect
                lfo1.frequency.linearRampToValueAtTime(value, this.context?.currentTime + 0.1 || 0);
                lfo2.frequency.linearRampToValueAtTime(value * 1.5, this.context?.currentTime + 0.1 || 0);
                console.log(`CHORUS: Setting LFO rates to ${value}Hz and ${value * 1.5}Hz`);
            },
            setDepth(value) {
                // Depth controls the amount of modulation
                const depthValue1 = value * 0.002; // Base depth for LFO1
                const depthValue2 = value * 0.003; // Slightly higher depth for LFO2
                lfoGain1.gain.linearRampToValueAtTime(depthValue1, this.context?.currentTime + 0.1 || 0);
                lfoGain2.gain.linearRampToValueAtTime(depthValue2, this.context?.currentTime + 0.1 || 0);
                console.log(`CHORUS: Setting depths to ${depthValue1} and ${depthValue2}`);
            }
        };
    }

    // Create delay effect
    createDelay() {
        const input = this.context.createGain();
        const output = this.context.createGain();
        const wet = this.context.createGain();
        const dry = this.context.createGain();

        // Set initial mix
        wet.gain.value = 0;
        dry.gain.value = 1;

        // Create delay node
        const delayNode = this.context.createDelay();
        delayNode.delayTime.value = 0.25;

        // Create feedback loop
        const feedback = this.context.createGain();
        feedback.gain.value = 0.3;

        // Connect delay network
        input.connect(dry);
        input.connect(delayNode);
        delayNode.connect(feedback);
        feedback.connect(delayNode);
        delayNode.connect(wet);

        // Mix wet and dry to output
        dry.connect(output);
        wet.connect(output);

        return {
            input,
            output,
            wet: wet.gain,
            feedback: feedback.gain,
            delayTime: delayNode.delayTime,
            setWet(value) {
                wet.gain.linearRampToValueAtTime(value, this.context?.currentTime + 0.1 || 0);
            }
        };
    }

    // Create reverb effect using convolver
    createReverb() {
        const input = this.context.createGain();
        const output = this.context.createGain();
        const wet = this.context.createGain();
        const dry = this.context.createGain();
        const preDelay = this.context.createDelay(5); // Max 5 seconds pre-delay

        // Set initial mix and parameters
        wet.gain.value = 0.2;
        dry.gain.value = 0.8;
        preDelay.delayTime.value = 0.03; // 30ms pre-delay default

        // Create convolver for reverb
        const convolver = this.context.createConvolver();

        // Set default parameters for impulse response generation
        const duration = 2;
        const decay = 2.5;

        // Connect reverb network with pre-delay (exactly like reverbTest.html)
        input.connect(dry);     // Direct dry path
        input.connect(preDelay); // Wet path through convolver
        preDelay.connect(convolver);
        convolver.connect(wet);

        // Mix wet and dry to output
        dry.connect(output);
        wet.connect(output);

        console.log(`REVERB: Connected reverb network - input → [dry → output] + [preDelay → convolver → wet → output]`);

        // Create a simple working impulse response immediately
        this.createSimpleImpulseResponse(convolver, duration, decay);

        // Then enhance with sophisticated impulse response asynchronously like reverbTest.html
        setTimeout(() => {
            console.log(`REVERB: Upgrading to sophisticated impulse response`);
            this.createReverbImpulseAsync(convolver, duration, decay);
        }, 1000);

        return {
            input,
            output,
            wet: wet.gain,
            dry: dry.gain,
            preDelay: preDelay.delayTime,
            convolver,
            setWet(value) {
                console.log(`REVERB: Setting wet=${value}, dry=${1 - value}`);

                // Ensure we always have some signal - if 100% wet and no convolver buffer, we get silence
                const hasValidBuffer = convolver.buffer && convolver.buffer.length > 0;
                console.log(`REVERB: Convolver has valid buffer:`, hasValidBuffer);

                if (!hasValidBuffer && value > 0.9) {
                    // If no valid buffer and trying to go full wet, keep some dry signal
                    console.log(`REVERB: No valid buffer detected, keeping some dry signal`);
                    wet.gain.linearRampToValueAtTime(0.7, this.context?.currentTime + 0.1 || 0);
                    dry.gain.linearRampToValueAtTime(0.3, this.context?.currentTime + 0.1 || 0);
                } else {
                    wet.gain.linearRampToValueAtTime(value, this.context?.currentTime + 0.1 || 0);
                    dry.gain.linearRampToValueAtTime(1 - value, this.context?.currentTime + 0.1 || 0);
                }

                // Verify the values were set
                setTimeout(() => {
                    console.log(`REVERB VERIFY: wet.gain.value=${wet.gain.value}, dry.gain.value=${dry.gain.value}`);
                }, 150);
            },
            setPreDelay(value) {
                preDelay.delayTime.linearRampToValueAtTime(value, this.context?.currentTime + 0.1 || 0);
            },
            setDecayTime(value) {
                // Regenerate the impulse response with the new decay time
                console.log(`Regenerating reverb impulse with decay time ${value}`);
                const duration = Math.max(1, value * 1.5); // Make duration longer than decay
                // Use the WebAudioEngine instance to call the method
                const engine = window.webAudioEngine || this;
                engine.createReverbImpulseAsync(convolver, duration, value);
            }
        };
    }

    // Create reverb impulse response using the same approach as reverbTest.html
    async createReverbImpulseAsync(convolver, duration = 2, decay = 2.5) {
        try {
            console.log(`Creating reverb impulse response: duration=${duration}s, decay=${decay}`);

            // Create offline context for rendering the impulse response (exactly like reverbTest.html)
            const offlineCtx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(
                2, // stereo
                this.context.sampleRate * duration,
                this.context.sampleRate
            );

            // Create buffer source for impulse
            const impulseSource = offlineCtx.createBufferSource();
            const impulseBuffer = offlineCtx.createBuffer(2, 1, offlineCtx.sampleRate);

            // Fill the buffer with a single impulse
            for (let channel = 0; channel < impulseBuffer.numberOfChannels; channel++) {
                const channelData = impulseBuffer.getChannelData(channel);
                channelData[0] = 1.0; // Impulse at time 0
            }

            impulseSource.buffer = impulseBuffer;

            // Create a gain envelope for the decay
            const envelope = offlineCtx.createGain();
            envelope.gain.setValueAtTime(1, 0);
            envelope.gain.exponentialRampToValueAtTime(0.001, duration);

            // Create filters for more natural reverb
            const lowpass = offlineCtx.createBiquadFilter();
            lowpass.type = 'lowpass';
            lowpass.frequency.value = 5000;
            lowpass.Q.value = 0.5;

            const highpass = offlineCtx.createBiquadFilter();
            highpass.type = 'highpass';
            highpass.frequency.value = 300;
            highpass.Q.value = 0.5;

            // Connect the nodes (exactly like reverbTest.html)
            impulseSource.connect(envelope);
            envelope.connect(lowpass);
            lowpass.connect(highpass);
            highpass.connect(offlineCtx.destination);

            // Start the source and begin rendering
            impulseSource.start();

            // When the rendering is complete, set the buffer to the convolver
            const renderedBuffer = await offlineCtx.startRendering();
            console.log('Reverb impulse response rendered successfully');

            // Apply noise to buffer for more realistic reverb (like reverbTest.html)
            const finalBuffer = this.applyNoiseToBuffer(renderedBuffer, decay);
            convolver.buffer = finalBuffer;
            console.log('Impulse response set to convolver');

        } catch (err) {
            console.error(`Error creating reverb impulse response: ${err.message}`);
            // Fall back to simple impulse response
            this.createSimpleImpulseResponse(convolver, duration, decay);
        }
    }

    // Apply noise to a buffer for more realistic reverb (from reverbTest.html)
    applyNoiseToBuffer(buffer, decay) {
        // Create a new buffer with the same specs
        const newBuffer = this.context.createBuffer(
            buffer.numberOfChannels,
            buffer.length,
            buffer.sampleRate
        );

        // Process each channel
        for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
            const inputData = buffer.getChannelData(channel);
            const outputData = newBuffer.getChannelData(channel);

            for (let i = 0; i < buffer.length; i++) {
                // Add some noise and apply decay
                const noise = Math.random() * 2 - 1;
                const decayFactor = Math.pow(1 - i / buffer.length, decay);
                outputData[i] = (inputData[i] + noise * 0.1) * decayFactor;
            }
        }

        return newBuffer;
    }

    // Render a more sophisticated reverb impulse response
    renderReverbImpulse(convolver, duration = 2, decay = 2.5) {
        try {
            console.log(`Creating reverb impulse: duration=${duration}s, decay=${decay}`);

            // Create offline context for rendering the impulse response
            const offlineCtx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(
                2, // stereo
                this.context.sampleRate * duration,
                this.context.sampleRate
            );

            // Create buffer source for impulse
            const impulseSource = offlineCtx.createBufferSource();
            const impulseBuffer = offlineCtx.createBuffer(2, 1, offlineCtx.sampleRate);

            // Fill the buffer with a single impulse
            for (let channel = 0; channel < impulseBuffer.numberOfChannels; channel++) {
                const channelData = impulseBuffer.getChannelData(channel);
                channelData[0] = 1.0; // Impulse at time 0
            }

            impulseSource.buffer = impulseBuffer;

            // Create a gain envelope for the decay
            const envelope = offlineCtx.createGain();
            envelope.gain.setValueAtTime(1, 0);
            envelope.gain.exponentialRampToValueAtTime(0.001, duration);

            // Create filters for more natural reverb
            const lowpass = offlineCtx.createBiquadFilter();
            lowpass.type = 'lowpass';
            lowpass.frequency.value = 5000;
            lowpass.Q.value = 0.5;

            const highpass = offlineCtx.createBiquadFilter();
            highpass.type = 'highpass';
            highpass.frequency.value = 300;
            highpass.Q.value = 0.5;

            // Create a bit of early reflections with a delay network
            const earlyReflections = [];
            for (let i = 0; i < 4; i++) {
                const delay = offlineCtx.createDelay();
                delay.delayTime.value = 0.005 + (i * 0.005);
                const gain = offlineCtx.createGain();
                gain.gain.value = 0.5 - (i * 0.1);
                earlyReflections.push({ delay, gain });
            }

            // Connect the nodes
            impulseSource.connect(envelope);
            envelope.connect(lowpass);
            lowpass.connect(highpass);

            // Connect early reflections
            earlyReflections.forEach(reflection => {
                highpass.connect(reflection.delay);
                reflection.delay.connect(reflection.gain);
                reflection.gain.connect(offlineCtx.destination);
            });

            // Also connect direct path
            highpass.connect(offlineCtx.destination);

            // Start the source and begin rendering
            impulseSource.start();

            // When the rendering is complete, set the buffer to the convolver
            offlineCtx.startRendering().then((renderedBuffer) => {
                console.log('Reverb impulse response rendered successfully');
                convolver.buffer = this.applyNoiseToBuffer(renderedBuffer, decay);
            }).catch((err) => {
                console.error('Error rendering reverb impulse response:', err);
            });
        } catch (err) {
            console.error('Error creating reverb impulse response:', err);
            // Fall back to simple impulse response
            this.createSimpleImpulseResponse(convolver, duration, decay);
        }
    }

    // Create a simple impulse response as fallback
    createSimpleImpulseResponse(convolver, duration = 2, decay = 2.5) {
        console.log('Creating simple impulse response as fallback');
        const sampleRate = this.context.sampleRate;
        const length = sampleRate * duration;
        const impulse = this.context.createBuffer(2, length, sampleRate);

        for (let channel = 0; channel < 2; channel++) {
            const channelData = impulse.getChannelData(channel);
            for (let i = 0; i < length; i++) {
                const n = i / length;
                channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - n, decay);
            }
        }

        convolver.buffer = impulse;
    }

    // Apply noise to a buffer for more realistic reverb
    applyNoiseToBuffer(buffer, decay) {
        // Create a new buffer with the same specs
        const newBuffer = this.context.createBuffer(
            buffer.numberOfChannels,
            buffer.length,
            buffer.sampleRate
        );

        // Process each channel
        for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
            const inputData = buffer.getChannelData(channel);
            const outputData = newBuffer.getChannelData(channel);

            for (let i = 0; i < buffer.length; i++) {
                // Add some noise and apply decay
                const noise = Math.random() * 2 - 1;
                const decayFactor = Math.pow(1 - i / buffer.length, decay);
                outputData[i] = (inputData[i] + noise * 0.1) * decayFactor;
            }
        }

        return newBuffer;
    }

    // Update track parameters
    updateTrackParams(trackId, params = {}) {
        const graph = this.getTrackGraph(trackId);

        if (params.volume !== undefined) {
            // Set volume directly for immediate effect
            console.log(`VOLUME: Setting immediate volume for track ${trackId} to ${params.volume}`);
            graph.volume.gain.value = params.volume;

            // Also schedule a ramp for smooth transitions
            graph.volume.gain.linearRampToValueAtTime(
                params.volume,
                this.context.currentTime + 0.1
            );

            // Also update all players associated with this track
            this.players.forEach((playerData, clipId) => {
                if (playerData.trackId === trackId) {
                    console.log(`VOLUME: Updating player ${clipId} volume to ${params.volume}`);
                    playerData.gainNode.gain.value = params.volume;
                    playerData.gainNode.gain.linearRampToValueAtTime(
                        params.volume,
                        this.context.currentTime + 0.1
                    );
                }
            });
        }

        if (params.pan !== undefined) {
            // CRITICAL: This is where panning happens
            // Set pan value directly for immediate effect
            console.log(`PANNING: Setting immediate pan for track ${trackId} from ${graph.stereoPanner.pan.value} to ${params.pan}`);
            graph.stereoPanner.pan.value = params.pan;

            // Also schedule a ramp for smooth transitions
            graph.stereoPanner.pan.linearRampToValueAtTime(
                params.pan,
                this.context.currentTime + 0.1
            );

            // Also update all players associated with this track
            this.players.forEach((playerData, clipId) => {
                if (playerData.trackId === trackId) {
                    console.log(`PANNING: Updating player ${clipId} pan to ${params.pan}`);
                    playerData.pannerNode.pan.value = params.pan;
                    playerData.pannerNode.pan.linearRampToValueAtTime(
                        params.pan,
                        this.context.currentTime + 0.1
                    );
                }
            });

            // Debug output to verify pan values
            setTimeout(() => {
                console.log(`PANNING CHECK: Current pan value for track ${trackId} is now ${graph.stereoPanner.pan.value}`);
            }, 200);
        }
    }

    // Create a simple test tone with panning
    createTestTone(pan = 0) {
        if (!this.context) return null;

        console.log(`TEST TONE: Creating test tone with pan=${pan}`);

        // Create oscillator
        const oscillator = this.context.createOscillator();
        oscillator.type = 'sine';
        oscillator.frequency.value = 440; // A4

        // Create gain node
        const gainNode = this.context.createGain();
        gainNode.gain.value = 0.2; // Low volume

        // Create stereo panner with explicit options
        const pannerOptions = { pan: pan };
        const stereoPanner = new StereoPannerNode(this.context, pannerOptions);
        console.log(`TEST TONE: Created panner with value: ${stereoPanner.pan.value}`);

        // Connect nodes
        oscillator.connect(gainNode);
        gainNode.connect(stereoPanner);
        stereoPanner.connect(this.masterGain);

        // Start oscillator
        oscillator.start();
        console.log(`TEST TONE: Started oscillator with panning ${stereoPanner.pan.value}`);

        // Return control object
        return {
            oscillator,
            gainNode,
            stereoPanner,
            stop() {
                console.log(`TEST TONE: Stopping test tone`);
                oscillator.stop();
                oscillator.disconnect();
                gainNode.disconnect();
                stereoPanner.disconnect();
            },
            setPan(value) {
                console.log(`TEST TONE: Changing pan from ${stereoPanner.pan.value} to ${value}`);
                stereoPanner.pan.value = value;
            }
        };
    }

    // Create a stereo test with left and right channels
    createStereoTest() {
        if (!this.context) return null;

        console.log(`STEREO TEST: Creating stereo test`);

        // Create left oscillator (pure sine wave)
        const leftOsc = this.context.createOscillator();
        leftOsc.type = 'sine';
        leftOsc.frequency.value = 440; // A4

        // Create right oscillator (slightly different frequency for clear stereo effect)
        const rightOsc = this.context.createOscillator();
        rightOsc.type = 'sine';
        rightOsc.frequency.value = 444; // Slightly higher for beating effect

        // Create gain nodes
        const leftGain = this.context.createGain();
        leftGain.gain.value = 0.2;

        const rightGain = this.context.createGain();
        rightGain.gain.value = 0.2;

        // Create panners
        const leftPanner = new StereoPannerNode(this.context, { pan: -1 }); // Full left
        const rightPanner = new StereoPannerNode(this.context, { pan: 1 }); // Full right

        // Connect nodes
        leftOsc.connect(leftGain).connect(leftPanner).connect(this.masterGain);
        rightOsc.connect(rightGain).connect(rightPanner).connect(this.masterGain);

        // Start oscillators
        leftOsc.start();
        rightOsc.start();
        console.log(`STEREO TEST: Started oscillators with L/R panning`);

        // Return control object
        return {
            leftOsc,
            rightOsc,
            leftPanner,
            rightPanner,
            stop() {
                console.log(`STEREO TEST: Stopping stereo test`);
                leftOsc.stop();
                rightOsc.stop();
                leftOsc.disconnect();
                rightOsc.disconnect();
                leftGain.disconnect();
                rightGain.disconnect();
                leftPanner.disconnect();
                rightPanner.disconnect();
            }
        };
    }

    // Play a one-shot drum sample by name through a specific track's graph
    // name: one of 'kick','snare','hihat','openhat','crash','ride','tom1','tom2','tom3','splash'
    // trackId: route through this track graph (effects/pan/volume). If missing, routes to master
    playDrumSampleByName(name, trackId = null, velocity = 0.9, when = null) {
        if (!this.context) return null;
        const buffer = this.drumBuffers?.[name];
        if (!buffer) {
            console.warn(`Drum sample not loaded for "${name}"; falling back to synthetic if scheduled via synth.`);
            return null;
        }

        const startTime = when ?? this.context.currentTime;

        const source = this.context.createBufferSource();
        source.buffer = buffer;

        const gain = this.context.createGain();
        // Simple envelope for one-shot
        gain.gain.setValueAtTime(0.0001, startTime);
        gain.gain.linearRampToValueAtTime(Math.min(1, velocity), startTime + 0.005);
        // Let the natural tail play; small fade to avoid clicks
        const endTime = startTime + buffer.duration;
        gain.gain.setValueAtTime(Math.min(1, velocity), Math.max(startTime + 0.01, endTime - 0.02));
        gain.gain.linearRampToValueAtTime(0.0001, endTime + 0.02);

        // Route
        const graph = trackId ? this.getTrackGraph(trackId) : null;
        const destination = graph?.inputBus || this.masterGain || this.context.destination;
        source.connect(gain).connect(destination);

        const voice = {
            source,
            gain,
            stop: (now = this.context.currentTime) => {
                const safeStopTime = Math.max(now, startTime);
                try { source.stop(safeStopTime); } catch (e) { }
                try { source.disconnect(); } catch (e) { }
                try { gain.disconnect(); } catch (e) { }
                this.unregisterVoice(voice);
            }
        };
        this.registerVoice(voice);
        try {
            source.addEventListener('ended', () => this.unregisterVoice(voice));
        } catch (e) {
            // Ignore in environments without addEventListener
        }

        // Schedule
        try {
            source.start(startTime);
            source.stop(endTime + 0.05);
        } catch (err) {
            console.warn('Failed to start/stop drum sample:', err);
        }

        // Return a small handle for optional cleanup
        return voice;
    }

    // Update track effects
    updateTrackEffects(trackId, effects = {}) {
        const graph = this.getTrackGraph(trackId);

        if (effects.eq) {
            const { low, mid, high } = effects.eq;
            if (low !== undefined) graph.eq.low.value = low;
            if (mid !== undefined) graph.eq.mid.value = mid;
            if (high !== undefined) graph.eq.high.value = high;
        }

        if (effects.compressor) {
            const { threshold, ratio, attack, release, knee, makeupGain, outputGain } = effects.compressor;
            console.log(`COMPRESSOR: Updating compressor for track ${trackId}:`, effects.compressor);

            if (threshold !== undefined) {
                graph.compressor.setThreshold(threshold);
            }
            if (ratio !== undefined) {
                graph.compressor.setRatio(ratio);
            }
            if (attack !== undefined) {
                graph.compressor.setAttack(attack);
            }
            if (release !== undefined) {
                graph.compressor.setRelease(release);
            }
            if (knee !== undefined) {
                graph.compressor.setKnee(knee);
            }
            if (makeupGain !== undefined) {
                graph.compressor.setMakeupGain(makeupGain);
            }
            if (outputGain !== undefined) {
                graph.compressor.setOutputGain(outputGain);
            }
        }

        if (effects.distortion) {
            const { preGain, drive, tone, mode } = effects.distortion;
            console.log(`DISTORTION: Updating distortion for track ${trackId}:`, effects.distortion);

            if (graph.distortion) {
                if (preGain !== undefined) {
                    graph.distortion.setPreGain(preGain);
                }
                if (drive !== undefined) {
                    graph.distortion.setDrive(drive);
                }
                if (tone !== undefined) {
                    graph.distortion.setTone(tone);
                }
                if (mode !== undefined) {
                    graph.distortion.setMode(mode);
                }
            } else {
                console.error(`DISTORTION: No distortion found for track ${trackId}`);
            }
        }

        if (effects.chorus !== undefined) {
            graph.chorus.setWet(Math.max(0, Math.min(1, effects.chorus)));
            console.log(`CHORUS: Setting chorus wet level to ${effects.chorus} for track ${trackId}`);
        }

        // Handle additional chorus parameters if provided
        if (effects.chorusParams) {
            if (effects.chorusParams.rate !== undefined) {
                const rate = Math.max(0.1, Math.min(10, effects.chorusParams.rate));
                graph.chorus.setRate(rate);
                console.log(`CHORUS: Setting rate to ${rate}Hz for track ${trackId}`);
            }

            if (effects.chorusParams.depth !== undefined) {
                const depth = Math.max(0, Math.min(1, effects.chorusParams.depth));
                graph.chorus.setDepth(depth);
                console.log(`CHORUS: Setting depth to ${depth} for track ${trackId}`);
            }
        }

        if (effects.delay !== undefined) {
            graph.delay.setWet(Math.max(0, Math.min(1, effects.delay)));
            console.log(`DELAY: Setting delay wet level to ${effects.delay} for track ${trackId}`);
        }

        // Handle additional delay parameters if provided
        if (effects.delayParams) {
            if (effects.delayParams.delayTime !== undefined) {
                const delayTime = Math.max(0.01, Math.min(2, effects.delayParams.delayTime));
                graph.delay.delayTime.linearRampToValueAtTime(
                    delayTime,
                    this.context.currentTime + 0.1
                );
                console.log(`DELAY: Setting delay time to ${delayTime}s for track ${trackId}`);
            }

            if (effects.delayParams.feedback !== undefined) {
                const feedback = Math.max(0, Math.min(0.95, effects.delayParams.feedback));
                graph.delay.feedback.linearRampToValueAtTime(
                    feedback,
                    this.context.currentTime + 0.1
                );
                console.log(`DELAY: Setting feedback to ${feedback} for track ${trackId}`);
            }
        }

        if (effects.reverb !== undefined) {
            const reverbValue = Math.max(0, Math.min(1, effects.reverb));
            console.log(`REVERB: Setting reverb wet level to ${reverbValue} for track ${trackId}`);

            // Debug: Check if reverb node exists and is connected
            console.log(`REVERB DEBUG: Reverb node exists:`, !!graph.reverb);
            console.log(`REVERB DEBUG: Reverb convolver buffer exists:`, !!graph.reverb.convolver?.buffer);
            console.log(`REVERB DEBUG: Current wet gain value:`, graph.reverb.wet?.value);
            console.log(`REVERB DEBUG: Current dry gain value:`, graph.reverb.dry?.value);

            // Apply the reverb setting
            graph.reverb.setWet(reverbValue);

            // Verify the setting was applied
            setTimeout(() => {
                console.log(`REVERB VERIFY: After setting - wet: ${graph.reverb.wet?.value}, dry: ${graph.reverb.dry?.value}`);
            }, 100);
        }

        // Handle additional reverb parameters if provided
        if (effects.reverbParams) {
            if (effects.reverbParams.preDelay !== undefined) {
                const preDelay = Math.max(0, Math.min(5, effects.reverbParams.preDelay));
                graph.reverb.setPreDelay(preDelay);
                console.log(`REVERB: Setting pre-delay to ${preDelay}s for track ${trackId}`);
            }

            if (effects.reverbParams.decayTime !== undefined) {
                const decayTime = Math.max(0.1, Math.min(10, effects.reverbParams.decayTime));
                graph.reverb.setDecayTime(decayTime);
                console.log(`REVERB: Setting decay time to ${decayTime}s for track ${trackId}`);
            }
        }
    }

    // Create audio player for a clip
    createPlayer(clipId, audioBuffer, volume = 0.8, pan = 0, trackId = null) {
        // Dispose existing player if any
        this.disposePlayer(clipId);

        // Create audio nodes
        const source = this.context.createBufferSource();
        source.buffer = audioBuffer;

        // fadeGainNode: dedicated GainNode for fade-in / fade-out envelopes.
        // Sits between source and the track volume gainNode so fades are
        // independent of the user's volume setting.
        const fadeGainNode = this.context.createGain();
        fadeGainNode.gain.value = 1; // default: no fade

        const gainNode = this.context.createGain();
        gainNode.gain.value = volume;

        // Create stereo panner
        const pannerOptions = { pan: pan };
        const pannerNode = new StereoPannerNode(this.context, pannerOptions);

        // Signal chain: source → fadeGainNode → gainNode → pannerNode → track/master
        source.connect(fadeGainNode);
        fadeGainNode.connect(gainNode);
        gainNode.connect(pannerNode);

        // Connect to track graph if provided, otherwise to master
        if (trackId && this.tracks.has(trackId)) {
            const trackGraph = this.getTrackGraph(trackId);
            pannerNode.connect(trackGraph.inputBus);
        } else {
            pannerNode.connect(this.masterGain);
        }

        // Store player data
        const playerData = {
            source,
            fadeGainNode,
            gainNode,
            pannerNode,
            audioBuffer,
            isPlaying: false,
            trackId,
            startTime: 0,
            offset: 0
        };

        this.players.set(clipId, playerData);
        return playerData;
    }

    // Reconnect existing player to track graph (in case it was created before track graph existed)
    reconnectPlayerToTrack(clipId, trackId) {
        const playerData = this.players.get(clipId);
        if (!playerData) {
            console.log(`🔌 RECONNECT: No player found for clip ${clipId}`);
            return;
        }

        if (!trackId) {
            console.log(`🔌 RECONNECT: No trackId provided`);
            return;
        }

        // Disconnect from current destination
        try {
            playerData.pannerNode.disconnect();
            console.log(`🔌 RECONNECT: Disconnected player ${clipId} from previous destination`);
        } catch (e) {
            console.log(`🔌 RECONNECT: Player ${clipId} was not connected`);
        }

        // Connect to track graph (this will create it if it doesn't exist)
        console.log(`🔌 RECONNECT: Getting track graph for track ${trackId}`);
        const trackGraph = this.getTrackGraph(trackId);
        console.log(`🔌 RECONNECT: Track graph obtained, reverb exists: ${!!trackGraph.reverb}`);

        playerData.pannerNode.connect(trackGraph.inputBus);
        console.log(`🔌 RECONNECT: Connected player ${clipId} to track ${trackId} input bus (with reverb)`);
    }

    // Start global playback of all audio clips
    startGlobalPlayback(clips, startTimeInBeats, tracks, bpm, options = {}) {
        // Stop current playback
        this.stopGlobalPlayback();

        console.log(`🎵 PLAYBACK: Starting global playback with ${clips.length} clips`);
        clips.forEach(clip => {
            console.log(`🎵 PLAYBACK: Clip ${clip.id} on track ${clip.trackId}, type: ${clip.type}`);
        });

        this.isPlaying = true;
        this.keepAlive = !!options?.keepAlive;
        this.startTime = this.context.currentTime;
        this.playbackTime = startTimeInBeats;
        this.activePlayerCount = 0;

        // Convert beats to seconds
        const startTimeInSeconds = this.beatsToSeconds(startTimeInBeats, bpm);

        // Filter audio clips
        const audioClips = clips.filter(clip => clip.type === 'audio' && clip.audioBuffer);

        // Start playback for each clip
        audioClips.forEach(clip => {
            const track = tracks.find(t => t.id === clip.trackId);
            console.log(`🎵 PLAYBACK: Processing clip ${clip.id}, track found:`, !!track, `track muted:`, track?.mute);

            if (!track || track.mute) {
                console.log(`🎵 PLAYBACK: Skipping clip ${clip.id} - no track or muted`);
                return;
            }

            // Ensure player exists
            let playerData = this.players.get(clip.id);
            if (!playerData) {
                console.log(`🎵 PLAYBACK: Creating new player for clip ${clip.id} on track ${track.id}`);
                playerData = this.createPlayer(
                    clip.id,
                    clip.audioBuffer,
                    track.volume,
                    track.pan,
                    track.id
                );
                console.log(`🎵 PLAYBACK: Player created for clip ${clip.id}, playerData exists: ${!!playerData}`);
            } else {
                console.log(`🎵 PLAYBACK: Using existing player for clip ${clip.id}`);
                // Ensure existing player is connected to track graph
                this.reconnectPlayerToTrack(clip.id, track.id);
            }

            if (!playerData) {
                console.log(`🎵 PLAYBACK: Failed to create player for clip ${clip.id}`);
                return;
            }

            // Calculate clip time range, respecting trim points
            const trimStart = (typeof clip.trimStart === 'number' && clip.trimStart >= 0) ? clip.trimStart : 0;
            const trimEnd = (typeof clip.trimEnd === 'number' && clip.trimEnd > trimStart)
                ? clip.trimEnd
                : clip.audioBuffer.duration;
            const trimmedDuration = trimEnd - trimStart; // seconds — one source cycle

            // Fade durations (seconds); default 0 = no fade
            const fadeIn = typeof clip.fadeIn === 'number' ? Math.max(0, clip.fadeIn) : 0;
            const fadeOut = typeof clip.fadeOut === 'number' ? Math.max(0, clip.fadeOut) : 0;

            // Total clip duration in seconds (may span multiple loop cycles)
            const totalClipBeats = clip.duration;
            const totalClipSeconds = this.beatsToSeconds(totalClipBeats, bpm);
            const clipStartInSeconds = this.beatsToSeconds(clip.start, bpm);

            // Determine how many loop iterations to schedule
            const isLooping = clip.loop && clip.sourceDuration > 0;
            const sourceDurSeconds = isLooping
                ? this.beatsToSeconds(clip.sourceDuration, bpm)
                : totalClipSeconds;
            const numIters = isLooping ? Math.ceil(totalClipSeconds / sourceDurSeconds) : 1;

            for (let iter = 0; iter < numIters; iter++) {
                const iterOffsetSec = iter * sourceDurSeconds;
                const iterClipStart = clipStartInSeconds + iterOffsetSec; // abs timeline seconds
                // Duration of this iteration (may be partial at the end)
                const remainingSec = totalClipSeconds - iterOffsetSec;
                const iterDuration = Math.min(trimmedDuration, remainingSec);
                if (iterDuration <= 0) break;

                const iterEnd = iterClipStart + iterDuration;
                if (iterEnd <= startTimeInSeconds) continue; // already past

                // Create a fresh player for each loop iteration (except iter 0 which reuses main player)
                let iterPlayerData = playerData;
                if (iter > 0) {
                    iterPlayerData = this.createPlayer(
                        `${clip.id}__loop_${iter}`,
                        clip.audioBuffer,
                        track.volume,
                        track.pan,
                        track.id
                    );
                    if (!iterPlayerData) continue;
                }

                if (iterClipStart >= startTimeInSeconds) {
                    // Entire iteration is after playhead
                    const delay = iterClipStart - startTimeInSeconds;
                    this.schedulePlayerStart(iterPlayerData, this.startTime + delay, trimStart, iterDuration, iter === 0 ? fadeIn : 0, 0);
                } else {
                    // Mid-iteration seek
                    const offsetIntoIter = startTimeInSeconds - iterClipStart;
                    if (offsetIntoIter < iterDuration) {
                        this.schedulePlayerStart(iterPlayerData, this.startTime, trimStart + offsetIntoIter, iterDuration - offsetIntoIter, 0, 0);
                    }
                }
            }
        });

        console.log(`🎵 Started playback of ${this.activePlayerCount} audio clips`);
    }

    // Schedule player to start playback.
    // duration  – optional: stop source after N seconds (trim support).
    // fadeIn    – optional: seconds for volume to ramp 0→1 from clip start.
    // fadeOut   – optional: seconds for volume to ramp 1→0 before clip end.
    // When the playhead starts mid-clip (offset > 0), fades that have already
    // passed are skipped gracefully.
    schedulePlayerStart(playerData, startTime, offset = 0, duration = undefined, fadeIn = 0, fadeOut = 0) {
        try {
            const sessionId = this.playbackSessionId;
            if (playerData.isPlaying) {
                playerData.source.stop();
                this.activePlayerCount--;
            }

            // Create new source node (they can only be started once)
            const source = this.context.createBufferSource();
            source.buffer = playerData.audioBuffer;

            // Get the track for this player to ensure we have latest volume/pan
            const trackId = playerData.trackId;
            if (trackId && this.tracks.has(trackId)) {
                const graph = this.tracks.get(trackId);

                if (graph.volume && graph.volume.gain) {
                    playerData.gainNode.gain.value = graph.volume.gain.value;
                }
                if (graph.stereoPanner && graph.stereoPanner.pan) {
                    playerData.pannerNode.pan.value = graph.stereoPanner.pan.value;
                }
            }

            // Connect new source into the fade gain node (which feeds gainNode → pannerNode)
            source.connect(playerData.fadeGainNode);

            // ── Schedule fade envelope on fadeGainNode ────────────────────
            const fg = playerData.fadeGainNode.gain;
            const clipDuration = typeof duration === 'number' && duration > 0
                ? duration
                : playerData.audioBuffer.duration - offset;
            const clipEnd = startTime + clipDuration;

            const safeIn = Math.max(0, fadeIn || 0);
            const safeOut = Math.max(0, fadeOut || 0);

            // Cancel ALL previously scheduled values (past and future) and
            // immediately hard-reset the gain to a known state right now.
            // Using context.currentTime ensures the reset takes effect before
            // the scheduled clip start time.
            const now = this.context.currentTime;
            fg.cancelScheduledValues(0);              // wipe entire automation timeline
            if (typeof fg.cancelAndHoldAtTime === 'function') {
                fg.cancelAndHoldAtTime(now);          // stop any in-progress ramp cleanly
            }
            fg.setValueAtTime(0, now);                // hard reset to 0 immediately

            // How far into the clip are we starting? (mid-clip seek)
            const playedSoFar = offset; // seconds of source already consumed

            if (safeIn > 0 && playedSoFar < safeIn) {
                // Fade-in zone not yet fully passed — start partway through the ramp
                const inStartGain = playedSoFar / safeIn;
                fg.setValueAtTime(inStartGain, startTime);
                fg.linearRampToValueAtTime(1, startTime + (safeIn - playedSoFar));
            } else {
                // No fade-in (or already past it) — jump straight to full volume
                fg.setValueAtTime(1, startTime);
            }

            if (safeOut > 0) {
                const fadeOutStartInClip = clipDuration - safeOut;
                if (fadeOutStartInClip > 0) {
                    // Normal case: flat body, then ramp down at the end
                    fg.setValueAtTime(1, startTime + fadeOutStartInClip);
                    fg.linearRampToValueAtTime(0, clipEnd);
                } else {
                    // Entire remaining region falls inside the fade-out zone
                    const remainingRatio = clipDuration / safeOut;
                    fg.setValueAtTime(remainingRatio, startTime);
                    fg.linearRampToValueAtTime(0, clipEnd);
                }
            }

            // Set up ended callback
            source.onended = () => {
                if (sessionId !== this.playbackSessionId) return;
                playerData.isPlaying = false;
                this.activePlayerCount--;
                if (!this.keepAlive && this.activePlayerCount <= 0 && this.isPlaying) {
                    this.isPlaying = false;
                    if (this.onPlaybackEnd) this.onPlaybackEnd();
                }
            };

            // Start playback; pass duration to stop at trimEnd boundary
            if (typeof duration === 'number' && duration > 0) {
                source.start(startTime, offset, duration);
            } else {
                source.start(startTime, offset);
            }
            playerData.source = source;
            playerData.isPlaying = true;
            playerData.startTime = startTime;
            playerData.offset = offset;
            this.activePlayerCount++;

        } catch (error) {
            console.error('Failed to start audio player:', error);
        }
    }

    // Stop global playback
    stopGlobalPlayback() {
        // Invalidate any pending source.onended callbacks from prior sessions
        this.playbackSessionId++;
        this.keepAlive = false;

        // Stop all scheduled/playing synth notes and drum one-shots
        this.stopAllVoices();

        this.players.forEach((playerData) => {
            if (playerData.isPlaying) {
                try {
                    playerData.source.stop();
                    playerData.isPlaying = false;
                } catch (error) {
                    console.warn('Failed to stop audio player:', error);
                }
            }
            // Reset fadeGainNode automation so the next play always starts clean.
            // Without this, a fade-out that ended at gain=0 would leave the node
            // stuck at 0 and the next playback would be silent.
            if (playerData.fadeGainNode) {
                try {
                    const fg = playerData.fadeGainNode.gain;
                    fg.cancelScheduledValues(0);
                    fg.setValueAtTime(1, this.context.currentTime);
                } catch (_) { }
            }
        });

        this.isPlaying = false;
        this.activePlayerCount = 0;

        // Remove ephemeral loop-iteration players (keyed as "clipId__loop_N")
        // so they don't accumulate across multiple playbacks.
        const loopKeys = [];
        this.players.forEach((_, key) => {
            if (String(key).includes('__loop_')) loopKeys.push(key);
        });
        loopKeys.forEach(k => this.players.delete(k));
    }

    // Get current playback time in beats
    getCurrentPlaybackTime(bpm) {
        if (!this.isPlaying) return this.playbackTime;

        const elapsedSeconds = this.context.currentTime - this.startTime;
        const elapsedBeats = this.secondsToBeats(elapsedSeconds, bpm);
        return this.playbackTime + elapsedBeats;
    }

    // Set playback end callback
    setPlaybackEndCallback(callback) {
        this.onPlaybackEnd = callback;
    }

    // Convert beats to seconds
    beatsToSeconds(beats, bpm) {
        return (beats * 60) / bpm;
    }

    // Convert seconds to beats
    secondsToBeats(seconds, bpm) {
        return (seconds * bpm) / 60;
    }

    // Dispose player
    disposePlayer(clipId) {
        const playerData = this.players.get(clipId);
        if (!playerData) return;

        if (playerData.isPlaying) {
            try {
                playerData.source.stop();
            } catch (error) {
                console.warn(`Failed to stop player ${clipId}:`, error);
            }
        }

        this.players.delete(clipId);
    }

    // Dispose all players
    disposeAllPlayers() {
        this.stopGlobalPlayback();
        this.players.clear();
    }

    // Create synth for a track
    createSynth(trackId, instrument = 'piano', soundfontInstrumentName = null) {
        // Dispose existing synth if any
        this.disposeSynth(trackId);

        // Get track graph
        const trackGraph = this.getTrackGraph(trackId);

        // Create synth data based on instrument type
        let oscillatorType, attackTime, decayTime, sustainLevel, releaseTime;

        switch (instrument) {
            case 'piano':
                oscillatorType = 'triangle';
                attackTime = 0.02;
                decayTime = 0.1;
                sustainLevel = 0.3;
                releaseTime = 1;
                break;
            case 'drums':
                oscillatorType = 'sine';
                attackTime = 0.001;
                decayTime = 0.4;
                sustainLevel = 0.01;
                releaseTime = 1.4;
                break;
            case 'bass':
                oscillatorType = 'sawtooth';
                attackTime = 0.02;
                decayTime = 0.3;
                sustainLevel = 0.7;
                releaseTime = 0.8;
                break;
            case 'synth':
                oscillatorType = 'square';
                attackTime = 0.1;
                decayTime = 0.2;
                sustainLevel = 0.5;
                releaseTime = 0.8;
                break;
            default:
                oscillatorType = 'sine';
                attackTime = 0.05;
                decayTime = 0.2;
                sustainLevel = 0.5;
                releaseTime = 0.5;
        }

        // Use provided soundfont instrument name, or map instrument types to default soundfont instruments
        if (!soundfontInstrumentName) {
            if (instrument === 'piano') {
                soundfontInstrumentName = 'acoustic_grand_piano';
            }
            // Add more default mappings as needed for other instruments
        }

        // Create synth data
        const synthData = {
            trackId,
            instrument,
            soundfontInstrumentName,
            oscillatorType,
            attackTime,
            decayTime,
            sustainLevel,
            releaseTime,
            activeNotes: new Map(),

            // Play note
            triggerAttackRelease: (note, duration, time, velocity = 1.0) => {
                const frequency = this.noteToFrequency(note);
                const now = this.context.currentTime;
                const startTime = time || now;
                const endTime = startTime + duration;

                // Special handling for drums
                if (instrument === 'drums') {
                    return this.playDrumSound(note, frequency, startTime, endTime, velocity, trackGraph.inputBus, attackTime, decayTime, sustainLevel, releaseTime);
                }

                // Special handling for instruments with soundfont
                if (soundfontInstrumentName && this.soundfontInstruments[soundfontInstrumentName]) {
                    try {
                        const soundfontInst = this.soundfontInstruments[soundfontInstrumentName];

                        // soundfont-player expects note names like "C4", not MIDI numbers
                        // The 'note' parameter is already in the correct format (e.g., "C4")

                        // Play the soundfont note using the documented API
                        // According to docs: player.play(note, when, options)
                        const audioNode = soundfontInst.play(note, startTime, {
                            duration: duration,
                            gain: velocity
                        });

                        // The soundfont-player connects directly to audioContext.destination by default
                        // To route through our track effects, we need to reconnect it
                        if (audioNode && audioNode.connect) {
                            try {
                                // Disconnect from default destination
                                audioNode.disconnect();
                                // Connect to our track input bus
                                audioNode.connect(trackGraph.inputBus);
                            } catch (e) {
                                console.warn('Could not reconnect soundfont audio node:', e);
                            }
                        }

                        const voice = {
                            node: audioNode,
                            stop: (stopNow = this.context.currentTime) => {
                                const safeStopTime = Math.max(stopNow, startTime);
                                try {
                                    if (audioNode && typeof audioNode.stop === 'function') {
                                        audioNode.stop(safeStopTime);
                                    } else if (audioNode?.source && typeof audioNode.source.stop === 'function') {
                                        audioNode.source.stop(safeStopTime);
                                    }
                                } catch (e) { }
                                try { audioNode?.disconnect?.(); } catch (e) { }
                                this.unregisterVoice(voice);
                            }
                        };
                        this.registerVoice(voice);
                        try {
                            const source = audioNode?.source;
                            if (source) {
                                const previousOnEnded = source.onended;
                                source.onended = (...args) => {
                                    try {
                                        if (typeof previousOnEnded === 'function') previousOnEnded(...args);
                                    } finally {
                                        this.unregisterVoice(voice);
                                    }
                                };
                            }
                        } catch (e) { }

                        return voice;
                    } catch (error) {
                        console.warn('Soundfont playback failed, falling back to oscillator:', error);
                        console.error(error);
                        // Fall through to oscillator-based synth
                    }
                }

                // Standard synth playback for non-drum, non-piano instruments (or piano fallback)
                // Create oscillator
                const oscillator = this.context.createOscillator();
                oscillator.type = oscillatorType;
                oscillator.frequency.value = frequency;

                // Create envelope
                const envelope = this.context.createGain();
                envelope.gain.value = 0;

                // Connect oscillator to envelope to track input
                oscillator.connect(envelope);
                envelope.connect(trackGraph.inputBus);

                // Set up envelope
                const attackEndTime = startTime + attackTime;
                const decayEndTime = attackEndTime + decayTime;

                // Attack
                envelope.gain.linearRampToValueAtTime(0.0001, startTime);
                envelope.gain.linearRampToValueAtTime(velocity, attackEndTime);

                // Decay to sustain
                envelope.gain.linearRampToValueAtTime(sustainLevel * velocity, decayEndTime);

                // Release
                envelope.gain.linearRampToValueAtTime(sustainLevel * velocity, endTime);
                envelope.gain.linearRampToValueAtTime(0.0001, endTime + releaseTime);

                // Start and stop oscillator
                oscillator.start(startTime);
                oscillator.stop(endTime + releaseTime);

                const voice = {
                    oscillator,
                    envelope,
                    stop: (stopNow = this.context.currentTime) => {
                        const safeStopTime = Math.max(stopNow, startTime);
                        const fadeTime = 0.01;
                        try {
                            envelope.gain.cancelScheduledValues(safeStopTime);
                            envelope.gain.setValueAtTime(envelope.gain.value || 0.0001, safeStopTime);
                            envelope.gain.linearRampToValueAtTime(0.0001, safeStopTime + fadeTime);
                        } catch (e) { }
                        try { oscillator.stop(safeStopTime + fadeTime); } catch (e) { }
                        try { oscillator.disconnect(); } catch (e) { }
                        try { envelope.disconnect(); } catch (e) { }
                        synthData?.activeNotes.delete(note);
                        this.unregisterVoice(voice);
                    }
                };
                this.registerVoice(voice);

                // Clean up
                oscillator.onended = () => {
                    try { oscillator.disconnect(); } catch (e) { }
                    try { envelope.disconnect(); } catch (e) { }
                    synthData?.activeNotes.delete(note);
                    this.unregisterVoice(voice);
                };

                // Store active note
                synthData?.activeNotes.set(note, { oscillator, envelope });

                return voice;
            },

            // Start a note and keep it held until triggerRelease is called
            triggerAttack: (note, time, velocity = 1.0) => {
                // Stop any existing note with the same name first
                if (synthData.activeNotes.has(note)) {
                    const prev = synthData.activeNotes.get(note);
                    try { prev.voice?.stop?.(); } catch (e) { }
                }

                const frequency = this.noteToFrequency(note);
                const now = this.context.currentTime;
                const startTime = time || now;

                if (instrument === 'drums') {
                    return this.playDrumSound(note, frequency, startTime, startTime + 2, velocity, trackGraph.inputBus, attackTime, decayTime, sustainLevel, releaseTime);
                }

                // Soundfont path — play with a long duration, stop on release
                if (soundfontInstrumentName && this.soundfontInstruments[soundfontInstrumentName]) {
                    try {
                        const soundfontInst = this.soundfontInstruments[soundfontInstrumentName];
                        const audioNode = soundfontInst.play(note, startTime, {
                            duration: 30,
                            gain: velocity
                        });
                        if (audioNode && audioNode.connect) {
                            try {
                                audioNode.disconnect();
                                audioNode.connect(trackGraph.inputBus);
                            } catch (e) { }
                        }
                        const voice = {
                            node: audioNode,
                            stop: (stopNow = this.context.currentTime) => {
                                try {
                                    if (audioNode && typeof audioNode.stop === 'function') audioNode.stop(stopNow);
                                    else if (audioNode?.source && typeof audioNode.source.stop === 'function') audioNode.source.stop(stopNow);
                                } catch (e) { }
                                try { audioNode?.disconnect?.(); } catch (e) { }
                                synthData?.activeNotes.delete(note);
                                this.unregisterVoice(voice);
                            }
                        };
                        this.registerVoice(voice);
                        synthData?.activeNotes.set(note, { node: audioNode, voice });
                        return voice;
                    } catch (error) {
                        console.warn('Soundfont triggerAttack failed, falling back to oscillator:', error);
                    }
                }

                // Oscillator path — sustain indefinitely until release
                const oscillator = this.context.createOscillator();
                oscillator.type = oscillatorType;
                oscillator.frequency.value = frequency;
                const envelope = this.context.createGain();
                envelope.gain.value = 0;
                oscillator.connect(envelope);
                envelope.connect(trackGraph.inputBus);

                const attackEndTime = startTime + attackTime;
                const decayEndTime = attackEndTime + decayTime;
                envelope.gain.linearRampToValueAtTime(0.0001, startTime);
                envelope.gain.linearRampToValueAtTime(velocity, attackEndTime);
                envelope.gain.linearRampToValueAtTime(sustainLevel * velocity, decayEndTime);

                oscillator.start(startTime);

                const voice = {
                    oscillator,
                    envelope,
                    stop: (stopNow = this.context.currentTime) => {
                        const fadeTime = releaseTime || 0.05;
                        try {
                            envelope.gain.cancelScheduledValues(stopNow);
                            envelope.gain.setValueAtTime(envelope.gain.value || 0.0001, stopNow);
                            envelope.gain.linearRampToValueAtTime(0.0001, stopNow + fadeTime);
                        } catch (e) { }
                        try { oscillator.stop(stopNow + fadeTime); } catch (e) { }
                        try { oscillator.disconnect(); } catch (e) { }
                        try { envelope.disconnect(); } catch (e) { }
                        synthData?.activeNotes.delete(note);
                        this.unregisterVoice(voice);
                    }
                };
                this.registerVoice(voice);
                synthData?.activeNotes.set(note, { oscillator, envelope, voice });
                return voice;
            },

            // Release a held note started with triggerAttack
            triggerRelease: (note, time) => {
                const active = synthData?.activeNotes.get(note);
                if (!active) return;
                const stopTime = time || this.context.currentTime;
                if (active.voice && typeof active.voice.stop === 'function') {
                    active.voice.stop(stopTime);
                } else {
                    // Fallback: manually fade out
                    const fadeTime = releaseTime || 0.05;
                    if (active.envelope) {
                        try {
                            active.envelope.gain.cancelScheduledValues(stopTime);
                            active.envelope.gain.setValueAtTime(active.envelope.gain.value || 0.0001, stopTime);
                            active.envelope.gain.linearRampToValueAtTime(0.0001, stopTime + fadeTime);
                        } catch (e) { }
                    }
                    if (active.oscillator) {
                        try { active.oscillator.stop(stopTime + fadeTime); } catch (e) { }
                        try { active.oscillator.disconnect(); } catch (e) { }
                    }
                    if (active.envelope) {
                        try { active.envelope.disconnect(); } catch (e) { }
                    }
                    synthData?.activeNotes.delete(note);
                }
            }
        };

        // Store synth data
        this.synths.set(trackId, synthData);

        console.log(`✅ Synth created for track ${trackId} with instrument ${instrument}`);

        return synthData;
    }

    // Get synth for a track
    getSynth(trackId) {
        return this.synths.get(trackId);
    }

    // Dispose synth
    disposeSynth(trackId) {
        const synthData = this.synths.get(trackId);
        if (!synthData) return;

        // Stop all active notes
        synthData.activeNotes.forEach(({ oscillator, envelope }) => {
            try {
                oscillator.stop();
                oscillator.disconnect();
                envelope.disconnect();
            } catch (error) {
                console.warn(`Error stopping synth note:`, error);
            }
        });

        this.synths.delete(trackId);
    }

    // Dispose all synths
    disposeAllSynths() {
        this.synths.forEach((_, trackId) => this.disposeSynth(trackId));
    }

    // Convert note name to frequency
    noteToFrequency(note) {
        const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const octave = parseInt(note.slice(-1));
        const noteIndex = notes.indexOf(note.slice(0, -1));

        if (noteIndex === -1) return 440; // Default to A4

        // A4 is 440Hz, which is note 'A' in octave 4
        return 440 * Math.pow(2, (octave - 4) + (noteIndex - 9) / 12);
    }

    // Convert note name to MIDI number (for soundfont)
    noteNameToMidi(noteName) {
        const noteOrder = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const match = noteName.match(/^([A-G]#?)(\d)$/);
        if (!match) return 60; // Default to C4
        const [, pitch, octaveStr] = match;
        const octave = parseInt(octaveStr);
        const pitchIndex = noteOrder.indexOf(pitch);
        return (octave + 1) * 12 + pitchIndex;
    }

    // Play drum sound. Prefer one-shot samples if available; fall back to synthesis
    // Uses different techniques for different drum types based on note letter
    playDrumSound(note, frequency, startTime, endTime, velocity, destination, attackTime, decayTime, sustainLevel, releaseTime) {
        // Map MIDI notes to drum types based on note name
        // 36=kick(C), 38=snare(D), 42=hihat(F#), 46=openhat(A#), 49=crash(C#), 51=ride(D#), toms(A/B/G)
        let isDrumType = 'other';
        if (note.includes('C')) isDrumType = 'kick'; // C notes = kick
        else if (note.includes('D')) isDrumType = 'snare'; // D notes = snare
        else if (note.includes('F#') || note.includes('Gb')) isDrumType = 'hihat'; // F# = closed hihat
        else if (note.includes('A#') || note.includes('Bb')) isDrumType = 'openhat'; // A# = open hihat
        else if (note.includes('C#') || note.includes('Db')) isDrumType = 'crash'; // C# = crash
        else if (note.includes('D#') || note.includes('Eb')) isDrumType = 'ride'; // D# = ride
        else if (note.includes('A')) isDrumType = 'tom'; // A = tom
        else if (note.includes('B')) isDrumType = 'tom'; // B = tom
        else if (note.includes('G')) isDrumType = 'tom'; // G = tom

        // If a matching sample exists, use it as a one-shot
        if (this.drumBuffers && this.drumBuffers[isDrumType]) {
            const buffer = this.drumBuffers[isDrumType];
            const source = this.context.createBufferSource();
            source.buffer = buffer;

            const envelope = this.context.createGain();
            envelope.gain.setValueAtTime(0.0001, startTime);
            envelope.gain.linearRampToValueAtTime(Math.min(1, velocity), startTime + 0.005);
            // Let natural length play
            const oneShotEnd = startTime + buffer.duration;
            envelope.gain.setValueAtTime(Math.min(1, velocity), Math.max(startTime + 0.01, oneShotEnd - 0.02));
            envelope.gain.linearRampToValueAtTime(0.0001, oneShotEnd + 0.02);

            source.connect(envelope).connect(destination);
            try {
                source.start(startTime);
                source.stop(oneShotEnd + 0.05);
            } catch (e) {
                console.warn('Error starting drum sample:', e);
            }

            const voice = {
                sources: [source],
                envelope,
                stop: (stopNow = this.context.currentTime) => {
                    const safeStopTime = Math.max(stopNow, startTime);
                    const fadeTime = 0.01;
                    try {
                        envelope.gain.cancelScheduledValues(safeStopTime);
                        envelope.gain.setValueAtTime(envelope.gain.value || 0.0001, safeStopTime);
                        envelope.gain.linearRampToValueAtTime(0.0001, safeStopTime + fadeTime);
                    } catch (e) { }
                    try { source.stop(safeStopTime + fadeTime); } catch (e) { }
                    try { source.disconnect(); } catch (e) { }
                    try { envelope.disconnect(); } catch (e) { }
                    this.unregisterVoice(voice);
                }
            };
            this.registerVoice(voice);
            try {
                source.addEventListener('ended', () => this.unregisterVoice(voice));
            } catch (e) { }
            return voice;
        }

        const envelope = this.context.createGain();
        envelope.gain.value = 0;
        envelope.connect(destination);

        const sources = [];

        // Create appropriate sound based on drum type (synthetic fallback)
        switch (isDrumType) {
            case 'kick':
                // Kick: Low frequency oscillator with pitch sweep
                const kickOsc = this.context.createOscillator();
                kickOsc.type = 'sine';
                kickOsc.frequency.setValueAtTime(150, startTime);
                kickOsc.frequency.exponentialRampToValueAtTime(40, startTime + 0.05);
                kickOsc.connect(envelope);
                sources.push(kickOsc);

                // Envelope: Fast attack, quick decay
                envelope.gain.setValueAtTime(0, startTime);
                envelope.gain.linearRampToValueAtTime(velocity * 1.5, startTime + 0.001);
                envelope.gain.exponentialRampToValueAtTime(0.001, startTime + 0.4);
                break;

            case 'snare':
                // Snare: Pitched oscillator + noise
                const snareOsc = this.context.createOscillator();
                snareOsc.type = 'triangle';
                snareOsc.frequency.setValueAtTime(200, startTime);
                snareOsc.frequency.exponentialRampToValueAtTime(100, startTime + 0.05);
                snareOsc.connect(envelope);
                sources.push(snareOsc);

                // Add noise for snare snap
                const snareNoise = this.createNoiseBuffer(0.3);
                const snareSource = this.context.createBufferSource();
                snareSource.buffer = snareNoise;
                const snareNoiseFilter = this.context.createBiquadFilter();
                snareNoiseFilter.type = 'highpass';
                snareNoiseFilter.frequency.value = 2000;
                snareSource.connect(snareNoiseFilter);
                snareNoiseFilter.connect(envelope);
                sources.push(snareSource);

                envelope.gain.setValueAtTime(0, startTime);
                envelope.gain.linearRampToValueAtTime(velocity, startTime + 0.001);
                envelope.gain.exponentialRampToValueAtTime(0.001, startTime + 0.2);
                break;

            case 'hihat':
                // Hi-hat: High-pass filtered noise (short)
                const hihatNoise = this.createNoiseBuffer(0.1);
                const hihatSource = this.context.createBufferSource();
                hihatSource.buffer = hihatNoise;
                const hihatFilter = this.context.createBiquadFilter();
                hihatFilter.type = 'highpass';
                hihatFilter.frequency.value = 7000;
                hihatSource.connect(hihatFilter);
                hihatFilter.connect(envelope);
                sources.push(hihatSource);

                envelope.gain.setValueAtTime(0, startTime);
                envelope.gain.linearRampToValueAtTime(velocity * 0.7, startTime + 0.001);
                envelope.gain.exponentialRampToValueAtTime(0.001, startTime + 0.05);
                break;

            case 'openhat':
                // Open hi-hat: High-pass filtered noise (longer)
                const openhatNoise = this.createNoiseBuffer(0.4);
                const openhatSource = this.context.createBufferSource();
                openhatSource.buffer = openhatNoise;
                const openhatFilter = this.context.createBiquadFilter();
                openhatFilter.type = 'highpass';
                openhatFilter.frequency.value = 6000;
                openhatSource.connect(openhatFilter);
                openhatFilter.connect(envelope);
                sources.push(openhatSource);

                envelope.gain.setValueAtTime(0, startTime);
                envelope.gain.linearRampToValueAtTime(velocity * 0.6, startTime + 0.001);
                envelope.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);
                break;

            case 'crash':
                // Crash cymbal: Band-pass filtered noise (long)
                const crashNoise = this.createNoiseBuffer(1.5);
                const crashSource = this.context.createBufferSource();
                crashSource.buffer = crashNoise;
                const crashFilter = this.context.createBiquadFilter();
                crashFilter.type = 'bandpass';
                crashFilter.frequency.value = 5000;
                crashFilter.Q.value = 0.5;
                crashSource.connect(crashFilter);
                crashFilter.connect(envelope);
                sources.push(crashSource);

                envelope.gain.setValueAtTime(0, startTime);
                envelope.gain.linearRampToValueAtTime(velocity * 0.8, startTime + 0.01);
                envelope.gain.exponentialRampToValueAtTime(0.001, startTime + 1.2);
                break;

            case 'ride':
                // Ride cymbal: Band-pass filtered noise (sustained)
                const rideNoise = this.createNoiseBuffer(1.0);
                const rideSource = this.context.createBufferSource();
                rideSource.buffer = rideNoise;
                const rideFilter = this.context.createBiquadFilter();
                rideFilter.type = 'bandpass';
                rideFilter.frequency.value = 4000;
                rideFilter.Q.value = 1;
                rideSource.connect(rideFilter);
                rideFilter.connect(envelope);
                sources.push(rideSource);

                envelope.gain.setValueAtTime(0, startTime);
                envelope.gain.linearRampToValueAtTime(velocity * 0.5, startTime + 0.01);
                envelope.gain.exponentialRampToValueAtTime(0.001, startTime + 0.8);
                break;

            case 'tom':
                // Tom: Mid-frequency oscillator with pitch sweep
                const tomOsc = this.context.createOscillator();
                tomOsc.type = 'sine';
                const tomBaseFreq = frequency * 0.5; // Lower than pitch suggests
                tomOsc.frequency.setValueAtTime(tomBaseFreq, startTime);
                tomOsc.frequency.exponentialRampToValueAtTime(tomBaseFreq * 0.5, startTime + 0.08);
                tomOsc.connect(envelope);
                sources.push(tomOsc);

                envelope.gain.setValueAtTime(0, startTime);
                envelope.gain.linearRampToValueAtTime(velocity, startTime + 0.001);
                envelope.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);
                break;

            default:
                // Default: Simple sine tone
                const defaultOsc = this.context.createOscillator();
                defaultOsc.type = 'sine';
                defaultOsc.frequency.value = frequency;
                defaultOsc.connect(envelope);
                sources.push(defaultOsc);

                envelope.gain.setValueAtTime(0, startTime);
                envelope.gain.linearRampToValueAtTime(velocity, startTime + 0.001);
                envelope.gain.exponentialRampToValueAtTime(0.001, startTime + 0.2);
        }

        // Start all sources
        sources.forEach(source => {
            try {
                source.start(startTime);
                if (source instanceof OscillatorNode) {
                    source.stop(startTime + 2); // Give enough time for envelope
                }
            } catch (error) {
                console.warn('Error starting drum sound source:', error);
            }
        });

        // Cleanup
        const cleanupTime = startTime + 2;
        const cleanupTimer = setTimeout(() => {
            sources.forEach(source => {
                try {
                    source.disconnect();
                } catch (e) { }
            });
            try {
                envelope.disconnect();
            } catch (e) { }
            this.unregisterVoice(voice);
        }, (cleanupTime - this.context.currentTime) * 1000);

        const voice = {
            sources,
            envelope,
            stop: (stopNow = this.context.currentTime) => {
                const safeStopTime = Math.max(stopNow, startTime);
                const fadeTime = 0.01;
                try {
                    envelope.gain.cancelScheduledValues(safeStopTime);
                    envelope.gain.setValueAtTime(envelope.gain.value || 0.0001, safeStopTime);
                    envelope.gain.linearRampToValueAtTime(0.0001, safeStopTime + fadeTime);
                } catch (e) { }

                sources.forEach(source => {
                    try { source.stop(safeStopTime + fadeTime); } catch (e) { }
                });
                sources.forEach(source => {
                    try { source.disconnect(); } catch (e) { }
                });
                try { envelope.disconnect(); } catch (e) { }
                try { clearTimeout(cleanupTimer); } catch (e) { }
                this.unregisterVoice(voice);
            }
        };
        this.registerVoice(voice);

        return voice;
    }

    // Create noise buffer for percussion sounds
    createNoiseBuffer(duration = 0.5) {
        const sampleRate = this.context.sampleRate;
        const bufferSize = sampleRate * duration;
        const buffer = this.context.createBuffer(1, bufferSize, sampleRate);
        const data = buffer.getChannelData(0);

        for (let i = 0; i < bufferSize; i++) {
            data[i] = Math.random() * 2 - 1;
        }

        return buffer;
    }

    // Clean up all resources
    dispose() {
        this.stopGlobalPlayback();
        this.disposeAllPlayers();
        this.disposeAllSynths();

        // Disconnect master gain
        if (this.masterGain) {
            this.masterGain.disconnect();
        }

        // Close audio context
        if (this.context && this.context.state !== 'closed') {
            this.context.close();
        }

        this.context = null;
        this.masterGain = null;
        this.tracks.clear();
        this.players.clear();
        this.synths.clear();
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Offline mixdown export (full project render → AudioBuffer)
    // ─────────────────────────────────────────────────────────────────────────

    _takeMixdownSnapshot() {
        return {
            context: this.context,
            masterGain: this.masterGain,
            masterLimiter: this.masterLimiter,
            masterBoost: this.masterBoost,
            tracks: this.tracks,
            players: this.players,
            synths: this.synths,
            soundfontInstruments: this.soundfontInstruments,
            soundfontLoading: this.soundfontLoading,
            isPlaying: this.isPlaying,
            startTime: this.startTime,
            playbackTime: this.playbackTime,
            activePlayerCount: this.activePlayerCount,
            playbackSessionId: this.playbackSessionId,
            keepAlive: this.keepAlive,
            onPlaybackEnd: this.onPlaybackEnd,
            activeVoices: this.activeVoices,
        };
    }

    _restoreMixdownSnapshot(snap) {
        if (!snap) return;
        this.context = snap.context;
        this.masterGain = snap.masterGain;
        this.masterLimiter = snap.masterLimiter;
        this.masterBoost = snap.masterBoost;
        this.tracks = snap.tracks;
        this.players = snap.players;
        this.synths = snap.synths;
        this.soundfontInstruments = snap.soundfontInstruments;
        this.soundfontLoading = snap.soundfontLoading;
        this.isPlaying = snap.isPlaying;
        this.startTime = snap.startTime;
        this.playbackTime = snap.playbackTime;
        this.activePlayerCount = snap.activePlayerCount;
        this.playbackSessionId = snap.playbackSessionId;
        this.keepAlive = snap.keepAlive;
        this.onPlaybackEnd = snap.onPlaybackEnd;
        this.activeVoices = snap.activeVoices;
    }

    /**
     * Schedule volume/pan automation on the track graph (beats → seconds), same semantics as AutomationLane.
     */
    _scheduleTrackAutomationForOffline(graph, track, automationPoints, bpm, endTimeSec) {
        if (!graph?.volume?.gain || !graph?.stereoPanner?.pan) return;
        const ap = automationPoints?.[track.id];
        const toSec = (beats) => this.beatsToSeconds(beats, bpm);

        const scheduleParam = (param, nodes, defaultVal) => {
            param.cancelScheduledValues(0);
            if (!nodes || nodes.length === 0) {
                param.setValueAtTime(defaultVal, 0);
                param.setValueAtTime(defaultVal, endTimeSec);
                return;
            }
            const s = [...nodes].sort((a, b) => a.time - b.time);
            param.setValueAtTime(s[0].value, 0);
            for (let i = 0; i < s.length - 1; i++) {
                const tA = Math.min(endTimeSec, Math.max(0, toSec(s[i].time)));
                const tB = Math.min(endTimeSec, Math.max(0, toSec(s[i + 1].time)));
                param.setValueAtTime(s[i].value, tA);
                if (tB > tA) {
                    param.linearRampToValueAtTime(s[i + 1].value, tB);
                }
            }
            const last = s[s.length - 1];
            const tL = Math.min(endTimeSec, Math.max(0, toSec(last.time)));
            param.setValueAtTime(last.value, tL);
            param.setValueAtTime(last.value, endTimeSec);
        };

        scheduleParam(graph.volume.gain, ap?.volume, track.volume ?? 0.8);
        scheduleParam(graph.stereoPanner.pan, ap?.pan, track.pan ?? 0);
    }

    /**
     * Schedule all MIDI notes (same rules as App.jsx startPlaybackAt) for offline render from beat 0.
     */
    _scheduleMidiForOfflineMixdown(projectTracks, startBeat, bpm, engineStartTime) {
        const midiTracks = projectTracks.filter(track => track.type === 'midi' && !track.mute);
        const drumMap = instrumentConfigs?.drums?.drumMap || {};

        midiTracks.forEach(track => {
            const isDrumsTrack = track.instrument === 'drums' || (track.name || '').toLowerCase().includes('drum');

            let synth = null;
            if (!isDrumsTrack) {
                synth = this.getSynth(track.id) || this.createSynth(track.id, track.instrument, track.soundfontInstrument);
                if (!synth) return;
            }

            (track.clips || []).forEach(clip => {
                if (!clip.notes || clip.notes.length === 0) return;

                const contentOffset = Number(clip.contentOffset) || 0;
                const clipDurationBeats = Number(clip.duration) || Infinity;
                const isLooping = clip.loop && clip.sourceDuration > 0;
                const sourceDurBeats = isLooping ? clip.sourceDuration : clipDurationBeats;
                const numLoopIters = isLooping ? Math.ceil(clipDurationBeats / sourceDurBeats) : 1;

                for (let loopIter = 0; loopIter < numLoopIters; loopIter++) {
                    const loopOffsetBeats = loopIter * sourceDurBeats;

                    clip.notes.forEach((note) => {
                        const noteRelStart = (Number(note.start) || 0) - contentOffset;
                        if (noteRelStart + (note.duration || 0) <= 0) return;
                        if (noteRelStart >= sourceDurBeats) return;

                        const noteClipRelStart = loopOffsetBeats + noteRelStart;
                        if (noteClipRelStart >= clipDurationBeats) return;

                        const noteStartTime = (Number(clip.start) || 0) + noteClipRelStart - startBeat;
                        if (noteStartTime < 0) return;

                        try {
                            const noteVelocity = (note.velocity ?? 1.0) * track.volume;
                            const startTimeSeconds = this.beatsToSeconds(noteStartTime, bpm);
                            const absoluteStartTime = engineStartTime + startTimeSeconds;

                            if (isDrumsTrack && this.playDrumSampleByName) {
                                const drumName = drumMap[note.pitch]?.name;
                                if (drumName) {
                                    this.playDrumSampleByName(drumName, track.id, Math.min(1, noteVelocity), absoluteStartTime);
                                }
                                return;
                            }

                            const noteName = this.midiNumberToNoteName(note.pitch);
                            const maxNoteDur = clipDurationBeats - noteClipRelStart;
                            const noteDurBeats = Math.min(note.duration || 0.25, maxNoteDur);
                            const durationSeconds = noteDurBeats * (60 / bpm);

                            if (!synth) return;
                            synth.triggerAttackRelease(noteName, durationSeconds, absoluteStartTime, noteVelocity);
                        } catch (e) {
                            console.warn('Mixdown MIDI schedule error:', e);
                        }
                    });
                }
            });
        });
    }

    midiNumberToNoteName(midi) {
        const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const n = Math.max(0, Math.min(127, Math.round(midi)));
        const octave = Math.floor(n / 12) - 1;
        return notes[n % 12] + octave;
    }

    /**
     * Offline-render the full timeline (from beat 0) to a stereo AudioBuffer.
     * Temporarily swaps the engine onto an OfflineAudioContext, then restores the live graph.
     *
     * @param {Object} opts
     * @param {Array} opts.tracks
     * @param {number} opts.bpm
     * @param {number} opts.masterVolume 0..1
     * @param {Object} opts.automationPoints { [trackId]: { volume?: nodes, pan?: nodes } }
     * @param {number} [opts.tailSeconds=2]  reverb / release tail after last clip
     */
    async renderMixdown({ tracks, bpm, masterVolume = 0.8, automationPoints = {}, tailSeconds = 2 }) {
        if (!this.context) {
            await this.initialize();
        }
        const liveSampleRate = this.context.sampleRate || 48000;

        const hasContent = (tracks || []).some(t => (t.clips || []).length > 0);
        if (!hasContent) {
            console.warn('renderMixdown: no clips');
            return null;
        }

        const endBeat = calculateTimelineLength(tracks || []);
        let durationSec = this.beatsToSeconds(endBeat, bpm) + Math.max(0, tailSeconds);
        durationSec = Math.max(durationSec, 1);

        this.stopGlobalPlayback();
        const snapshot = this._takeMixdownSnapshot();

        const offline = new OfflineAudioContext({
            numberOfChannels: 2,
            length: Math.ceil(durationSec * liveSampleRate),
            sampleRate: liveSampleRate,
        });

        try {
            this.activeVoices = new Set();

            this.context = offline;
            this.tracks = new Map();
            this.players = new Map();
            this.synths = new Map();
            this.soundfontInstruments = {};
            this.soundfontLoading = {};
            this.isPlaying = false;
            this.activePlayerCount = 0;
            this.playbackSessionId++;

            this.masterGain = offline.createGain();
            this.masterGain.gain.value = masterVolume;

            this.masterLimiter = offline.createDynamicsCompressor();
            this.masterLimiter.threshold.value = -3;
            this.masterLimiter.knee.value = 0;
            this.masterLimiter.ratio.value = 20;
            this.masterLimiter.attack.value = 0.003;
            this.masterLimiter.release.value = 0.25;

            this.masterBoost = offline.createGain();
            this.masterBoost.gain.value = 1.6;

            this.masterGain.connect(this.masterLimiter);
            this.masterLimiter.connect(this.masterBoost);
            this.masterBoost.connect(offline.destination);

            const sfNames = new Set();
            (tracks || []).forEach(t => {
                if (t.type !== 'midi' || t.mute) return;
                const isDrums = t.instrument === 'drums' || (t.name || '').toLowerCase().includes('drum');
                if (isDrums) return;
                const name = t.soundfontInstrument || (t.instrument === 'piano' ? 'acoustic_grand_piano' : null);
                if (name) sfNames.add(name);
            });
            await Promise.all([...sfNames].map(n => this.loadSoundfontInstrument(n).catch(() => null)));

            (tracks || []).forEach(track => {
                if (track.mute) return;
                const graph = this.getTrackGraph(track.id);
                if (track.effects) {
                    try {
                        this.updateTrackEffects(track.id, track.effects);
                    } catch (e) {
                        console.warn('Mixdown: updateTrackEffects', e);
                    }
                }
                this._scheduleTrackAutomationForOffline(graph, track, automationPoints, bpm, durationSec);
            });

            const allClips = [];
            (tracks || []).forEach(track => {
                if (track.mute) return;
                (track.clips || []).forEach(clip => {
                    allClips.push({
                        ...clip,
                        trackId: track.id,
                        trackType: track.type,
                        trackVolume: track.volume,
                        trackPan: track.pan,
                    });
                });
            });

            const audioClips = allClips.filter(c => c.type === 'audio' && c.audioBuffer);
            this.startGlobalPlayback(audioClips, 0, tracks, bpm, { keepAlive: false });

            this._scheduleMidiForOfflineMixdown(tracks, 0, bpm, 0);

            const rendered = await offline.startRendering();
            return rendered;
        } finally {
            try {
                this.stopGlobalPlayback();
            } catch (_) { /* ignore */ }
            this.activeVoices.clear();
            this._restoreMixdownSnapshot(snapshot);
        }
    }
}
